import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import API_URL from '../config';
import './CustomerPreferences.css';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English', flag: '🇺🇸' },
  { value: 'es', label: 'Spanish', flag: '🇪🇸' },
  { value: 'fr', label: 'French', flag: '🇫🇷' },
  { value: 'de', label: 'German', flag: '🇩🇪' },
  { value: 'pt', label: 'Portuguese', flag: '🇵🇹' },
  { value: 'it', label: 'Italian', flag: '🇮🇹' },
  { value: 'ja', label: 'Japanese', flag: '🇯🇵' },
  { value: 'zh', label: 'Chinese', flag: '🇨🇳' }
];

const TIMEZONE_OPTIONS = [
  { label: 'Americas', options: [
    { value: 'America/New_York', label: 'America/New_York (UTC-5)' },
    { value: 'America/Chicago', label: 'America/Chicago (UTC-6)' },
    { value: 'America/Denver', label: 'America/Denver (UTC-7)' },
    { value: 'America/Los_Angeles', label: 'America/Los_Angeles (UTC-8)' },
    { value: 'America/Mexico_City', label: 'America/Mexico_City (UTC-6)' },
    { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (UTC-3)' }
  ]},
  { label: 'Europe', options: [
    { value: 'Europe/London', label: 'Europe/London (UTC+0)' },
    { value: 'Europe/Paris', label: 'Europe/Paris (UTC+1)' },
    { value: 'Europe/Berlin', label: 'Europe/Berlin (UTC+1)' },
    { value: 'Europe/Madrid', label: 'Europe/Madrid (UTC+1)' },
    { value: 'Europe/Rome', label: 'Europe/Rome (UTC+1)' },
    { value: 'Europe/Moscow', label: 'Europe/Moscow (UTC+3)' }
  ]},
  { label: 'Asia', options: [
    { value: 'Asia/Dubai', label: 'Asia/Dubai (UTC+4)' },
    { value: 'Asia/Kolkata', label: 'Asia/Kolkata (UTC+5:30)' },
    { value: 'Asia/Shanghai', label: 'Asia/Shanghai (UTC+8)' },
    { value: 'Asia/Tokyo', label: 'Asia/Tokyo (UTC+9)' },
    { value: 'Asia/Seoul', label: 'Asia/Seoul (UTC+9)' },
    { value: 'Asia/Singapore', label: 'Asia/Singapore (UTC+8)' }
  ]},
  { label: 'Pacific', options: [
    { value: 'Australia/Sydney', label: 'Australia/Sydney (UTC+11)' },
    { value: 'Pacific/Auckland', label: 'Pacific/Auckland (UTC+13)' }
  ]}
];

function CustomerPreferences() {
  const { customerId: routeCustomerId } = useParams();
  const { getAuthHeader, user } = useAuth();
  // /preferences (no id) means "my own", so nobody has to know their user id.
  const customerId = routeCustomerId || user?.userId || user?.id;
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);
  const [showTransactionalWarning, setShowTransactionalWarning] = useState(false);

  const { register, handleSubmit, watch, reset, setValue, formState: { isDirty } } = useForm({
    defaultValues: {
      marketingOptIn: false,
      transactionalOptIn: true,
      preferredLanguage: 'en',
      timezone: ''
    }
  });


  // Detect browser timezone on mount
  useEffect(() => {
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const loadPreferences = async () => {
      try {
        const [prefRes, summaryRes] = await Promise.all([
          fetch(`${API_URL}/api/customers/${customerId}/preferences`, {
            headers: getAuthHeader()
          }),
          fetch(`${API_URL}/api/customers/${customerId}/communication-summary`, {
            headers: getAuthHeader()
          })
        ]);

        if (!prefRes.ok || !summaryRes.ok) {
          throw new Error('Failed to load preferences');
        }

        const prefData = await prefRes.json();
        const summaryData = await summaryRes.json();

        setSummary(summaryData.data);

        // Reset form with loaded data or defaults
        reset({
          marketingOptIn: !prefData.data.marketingOptOut,
          transactionalOptIn: !prefData.data.transactionalOptOut,
          preferredLanguage: prefData.data.preferredLanguage,
          timezone: prefData.data.timezone || browserTimezone
        });

        setLoading(false);
      } catch (error) {
        console.error('Error loading preferences:', error);
        toast.error('Failed to load preferences');
        setLoading(false);
      }
    };

    loadPreferences();
  }, [customerId, reset, toast, getAuthHeader]);

  // The field is now "do you want these emails", so the warning belongs on
  // turning it OFF. It used to fire when the box was ticked, which -- because
  // the box was bound to the opt-OUT column -- meant switching booking emails
  // on asked whether you wanted to switch them off.
  const handleTransactionalToggle = (e) => {
    if (!e.target.checked) {
      setShowTransactionalWarning(true);
    }
  };

  const confirmTransactionalOptOut = () => {
    setValue('transactionalOptIn', false, { shouldDirty: true });
    setShowTransactionalWarning(false);
  };

  const cancelTransactionalOptOut = () => {
    // Put the toggle back. Previously this only hid the modal, so cancelling
    // left the box in its new position and the change was saved anyway.
    setValue('transactionalOptIn', true, { shouldDirty: true });
    setShowTransactionalWarning(false);
  };

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/customers/${customerId}/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        // The UI asks "email me"; the column stores the opposite.
        body: JSON.stringify({
          ...data,
          marketingOptOut: !data.marketingOptIn,
          transactionalOptOut: !data.transactionalOptIn
        })
      });

      if (!res.ok) {
        throw new Error('Failed to save preferences');
      }

      const result = await res.json();
      reset({
        ...result.data,
        marketingOptIn: !result.data.marketingOptOut,
        transactionalOptIn: !result.data.transactionalOptOut
      });
      toast.success('Preferences updated');
    } catch (error) {
      console.error('Error saving preferences:', error);
      toast.error('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const formatRelativeTime = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return <div className="preferences-loading">Loading preferences...</div>;
  }

  return (
    <div className="customer-preferences">
      <header className="preferences-header">
        <h1>Email Preferences</h1>
        <p className="customer-id">Customer ID: {customerId}</p>
      </header>

      {summary && (
        <div className="communication-summary">
          <div className="summary-stat">
            <div className="stat-value">{summary.totalSent}</div>
            <div className="stat-label">Emails Sent</div>
          </div>
          <div className="summary-stat">
            <div className="stat-value">{formatRelativeTime(summary.lastSentAt)}</div>
            <div className="stat-label">Last Email</div>
          </div>
          <div className="summary-stat">
            <div className="stat-value">{summary.upcomingCount}</div>
            <div className="stat-label">Upcoming</div>
          </div>
        </div>
      )}

      <form id="preferences-form" onSubmit={handleSubmit(onSubmit)} className="preferences-form">
        <section className="form-section">
          <h2>Email Types</h2>

          <div className="toggle-field">
            <div className="toggle-header">
              <label htmlFor="marketingOptIn" className="toggle-label">
                Email me about open weekends and offers
              </label>
              <input
                type="checkbox"
                id="marketingOptIn"
                {...register('marketingOptIn')}
                className="toggle-switch"
              />
            </div>
            <p className="field-description">
              Occasional emails when the house has openings at a discount, plus other news.
            </p>
            {!watch('marketingOptIn') && (
              <p className="field-note">
                You will still receive booking confirmations and essential account emails.
              </p>
            )}
          </div>

          <div className="toggle-field">
            <div className="toggle-header">
              <label htmlFor="transactionalOptIn" className="toggle-label">
                Booking &amp; Account Emails
              </label>
              <input
                type="checkbox"
                id="transactionalOptIn"
                className="toggle-switch"
                {...register('transactionalOptIn', { onChange: handleTransactionalToggle })}
              />
            </div>
            <p className="field-description">
              Reservation confirmations, reminders, and payment notifications
            </p>
          </div>

          {showTransactionalWarning && (
            <div className="warning-modal">
              <div className="warning-content">
                <div className="warning-icon">⚠</div>
                <h3>Turn off booking emails?</h3>
                <p>
                  Turning this off means you won't receive booking confirmations or payment
                  reminders. Are you sure?
                </p>
                <div className="warning-actions">
                  <button type="button" onClick={cancelTransactionalOptOut} className="btn-secondary">
                    Cancel
                  </button>
                  <button type="button" onClick={confirmTransactionalOptOut} className="btn-danger">
                    Turn Off
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>

        <section className="form-section">
          <h2>Localization</h2>

          <div className="form-field">
            <label htmlFor="preferredLanguage">Language</label>
            <select id="preferredLanguage" {...register('preferredLanguage')} className="form-select">
              {LANGUAGE_OPTIONS.map((lang) => (
                <option key={lang.value} value={lang.value}>
                  {lang.flag} {lang.label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-field">
            <label htmlFor="timezone">Timezone</label>
            <select id="timezone" {...register('timezone')} className="form-select">
              {TIMEZONE_OPTIONS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
        </section>

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={!isDirty || saving}>
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CustomerPreferences;
